import {
  createContext,
  LoaderFunctionArgs,
  MiddlewareFunction,
  redirect,
  useRouteLoaderData,
} from "react-router";
import {
  AccountsWithNameByApplication,
  AvailableAccountsRepresentation,
  OrganizationList,
  OrganizationRepresentation,
} from "../api/api";
import { getHalJSON } from "../api/digipost-fetch";
import { cached } from "../api/cache";
import { fetchEntrypoint } from "../entrypoint/EntrypointContext";
import { findUriIfExists, findURIAndReplaceOrigin } from "../entrypoint/link";
import { rootPath } from "../translation/useRootPath";

export const PROTECTED_ROUTE_ID = "protected";
export const ORGANIZATION_ROUTE_ID = "organization";

const fetchOrganizations = () =>
  cached("organizations", async () => {
    const entrypoint = await fetchEntrypoint();
    const url = findURIAndReplaceOrigin("organizations", entrypoint._links);

    return (await getHalJSON<OrganizationList>(url)).organizations;
  });

interface OrganizationsRouteData {
  organizations: OrganizationRepresentation[];
}

export const organizationsLoader =
  async (): Promise<OrganizationsRouteData> => ({
    organizations: await fetchOrganizations(),
  });

export const organizationContext = createContext<OrganizationRepresentation>();

export const organizationMiddleware: MiddlewareFunction = async (
  { params, context },
  next,
) => {
  const organizations = await fetchOrganizations();
  const organization = organizations.find(
    (org) => org.organization.orgnr === params.orgnr,
  );
  if (!organization) {
    throw redirect(rootPath(params.language));
  }

  context.set(organizationContext, organization);

  await next();
};

interface OrganizationRouteData {
  organization: OrganizationRepresentation;
}

export const organizationLoader = ({
  context,
}: LoaderFunctionArgs): OrganizationRouteData => ({
  organization: context.get(organizationContext),
});

export function useOrganizations(): OrganizationRepresentation[] {
  return (
    useRouteLoaderData<OrganizationsRouteData>(PROTECTED_ROUTE_ID)
      ?.organizations ?? []
  );
}

export function useOrganizationIfSelected(): OrganizationRepresentation | null {
  return (
    useRouteLoaderData<OrganizationRouteData>(ORGANIZATION_ROUTE_ID)
      ?.organization ?? null
  );
}

export function useSelectedOrganization(): OrganizationRepresentation {
  const organization = useOrganizationIfSelected();

  if (!organization) {
    throw new Error(
      "useSelectedOrganization must be used within a route that has an organization selected",
    );
  }

  return organization;
}

export const fetchAvailableAccounts = async (
  organization: OrganizationRepresentation,
): Promise<AccountsWithNameByApplication> => {
  const accountsUrl = findUriIfExists("accounts", organization._links);
  if (!accountsUrl) {
    return {};
  }

  return cached(
    accountsUrl,
    async () =>
      (await getHalJSON<AvailableAccountsRepresentation>(accountsUrl)).accounts,
  );
};
